<template>
  <header class="admin-topbar">
    <!-- Left: burger + quick actions -->
    <div class="left">
      <button class="icon-btn" @click="props.toggleSidebar" title="Toggle menu">
        <i class="fa-solid fa-bars" aria-hidden="true"></i>
      </button>

      <button class="text-btn pill">
        <i class="fa-regular fa-folder-open" aria-hidden="true"></i>
        <span>Workspace</span>
      </button>

      <div class="dropdown" @click="quickMenuOpen = !quickMenuOpen">
        <button class="text-btn pill">
          <i class="fa-solid fa-bolt" aria-hidden="true"></i>
          <span>Quick Menu</span>
          <i
            class="fa-solid"
            :class="quickMenuOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
            aria-hidden="true"
          ></i>
        </button>
        <div v-if="quickMenuOpen" class="dropdown-menu">
          <button class="dropdown-item" @click="goToClients">
            <i class="fa-regular fa-building"></i>
            <span>Clients</span>
          </button>
          <button v-if="canViewBilling" class="dropdown-item" @click="goToBilling">
            <i class="fa-regular fa-credit-card"></i>
            <span>Billing</span>
          </button>
          <button class="dropdown-item" @click="goToSupport">
            <i class="fa-regular fa-bell"></i>
            <span>Support</span>
          </button>
        </div>
      </div>

      <button class="icon-btn pill" title="Search">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Right: notifications + user menu -->
    <div class="right">
      <button class="icon-btn" title="Notifications" @click="goToLogs">
        <i class="fa-regular fa-bell" aria-hidden="true"></i>
        <span v-if="unreadCount > 0" class="badge">{{ unreadCount }}</span>
      </button>
      <button class="icon-btn" title="Messages">
        <i class="fa-regular fa-comments" aria-hidden="true"></i>
        <span class="badge">5</span>
      </button>

      <div class="user-dropdown" @click="userMenuOpen = !userMenuOpen">
        <div class="avatar">
          {{ userName.charAt(0).toUpperCase() }}
        </div>
        <span class="name is-hidden">{{ userName }} • {{ appName }}</span>
        <i
          class="fa-solid"
          :class="userMenuOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
          aria-hidden="true"
        ></i>

        <div v-if="userMenuOpen" class="dropdown-menu right-align">
          <button class="dropdown-item" @click.stop="goMyProfile">
            <i class="fa-regular fa-user"></i><span>My Profile</span>
          </button>
          <button class="dropdown-item" @click.stop="goMySettings">
            <i class="fa-solid fa-gear"></i><span>Settings</span>
          </button>
          <button class="dropdown-item">
            <i class="fa-regular fa-envelope"></i><span>My Messages</span>
          </button>
          <button class="dropdown-item" @click.stop="goToLogs">
            <i class="fa-regular fa-bell"></i><span>Notifications</span>
          </button>
          <button class="dropdown-item">
            <i class="fa-solid fa-lock"></i><span>Lock Screen</span>
          </button>
          <button class="dropdown-item" @click.stop="logout">
            <i class="fa-solid fa-right-from-bracket"></i><span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useUiStore } from '../../stores/ui'
import api from '../../api/client'
import { onBeforeUnmount } from 'vue'
import { getApplicationLabel } from '../../constants/applications'

onBeforeUnmount(() => clearInterval(timer))
const props = defineProps<{
  toggleSidebar: () => void
}>()

const router = useRouter()
const auth = useAuthStore()
const ui = useUiStore()

const quickMenuOpen = ref(false)
const userMenuOpen = ref(false)
let timer: ReturnType<typeof setInterval>

const userName = computed(() => auth.user?.full_name ?? 'Admin')
const appName = computed(() => getApplicationLabel(auth.application?.code))
const unreadCount = ref<number>(0)
const canViewBilling = computed(() =>
  auth.hasAnyPermission([
    'admin.billing.view',
    'admin.billing.subscriptions.view',
    'admin.billing.payments.view',
  ]),
)
const canViewSettings = computed(() =>
  auth.hasAnyPermission([
    'admin.settings.view',
    'admin.settings.manage',
    'admin.settings.users.view',
    'admin.settings.users.manage',
  ]),
)

async function loadUnreadCount() {
  try {
    const res = await api.get('/api/core/notifications/unread-count/')
    unreadCount.value = res.data?.unread_count ?? 0
  } catch (e: any) {
    // keep silent (topbar shouldn't spam errors)
    unreadCount.value = 0
  }
}

function goToLogs() {
  router.push({ name: 'admin.system.logs' })
}

function goToClients() {
  router.push({ name: 'admin.clients.index' })
  quickMenuOpen.value = false
}

function goToBilling() {
  if (!canViewBilling.value) {
    quickMenuOpen.value = false
    return
  }
  router.push({ name: 'admin.billing.index' })
  quickMenuOpen.value = false
}

function goToSupport() {
  router.push({ name: 'admin.support.index' })
  quickMenuOpen.value = false
}

function onNotifUpdated() { loadUnreadCount() }
window.addEventListener("notif-updated", onNotifUpdated)
onBeforeUnmount(() => window.removeEventListener("notif-updated", onNotifUpdated))


onMounted(async () => {
  timer = setInterval(loadUnreadCount, 15000)
  await loadUnreadCount()
})

const goMyProfile = () => {
  router.push({ name: "admin.dashboard.my-profile" })
  userMenuOpen.value = false
}

const goMySettings = () => {
  if (!canViewSettings.value) {
    userMenuOpen.value = false
    return
  }
  router.push({ name: 'admin.system.company-settings' })
  userMenuOpen.value = false
}

const logout = () => {
  auth.logout()
  ui.showToast('You have successfully logged out.', 'success')
  router.replace('/admin/login')
}
</script>
